import { useState } from 'react';
import { Skeleton } from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';
import { rootTranslation } from 'utils/format';
import { FormField } from 'components/common/Form/Fields/FormField/FormField';
import { useSearchTracksPartnersQuery } from 'services/tracks/tracks';
import { createQwireTrackPartnersCall } from 'redux-core/tracks/services';
import { AutocompleteWithEdit } from 'components/common/AutocompleteWithEdit/AutocompleteWithEdit';
import { useController, useWatch, useFormContext } from 'react-hook-form';

const t = rootTranslation('drawers.productionSetup.tracks.filter');

type TOption = {
  id: number;
  name: string;
};

export function LibraryField() {
  const [term, setTerm] = useState('');
  const { data, isLoading } = useSearchTracksPartnersQuery({ term, limit: 0 });
  const { field: partnerField } = useController({ name: 'partner' });
  const { setValue } = useFormContext();

  const { control } = useFormContext();
  const contentMode = useWatch({ name: 'contentMode', control });
  const isCreateMode = contentMode === 'create';

  const debouncedSetSearch = useDebouncedCallback((_, inputValue) => {
    if (!inputValue) {
      setValue('title', '');
      setValue('libraryTrackId', '');
      return;
    }
    setTerm(inputValue);
  }, 300);

  if (isLoading) {
    return <Skeleton />;
  }

  const options = [...(data.content ?? [])].sort((a, b) => a.name.localeCompare(b.name));

  const handleCreatePartner = async (value: string | TOption) => {
    const name = typeof value === 'string' ? value : value?.name;
    if (!name) return;
    const textKey = name.toLowerCase().replace(/\s+/g, '_');
    const newValue = await createQwireTrackPartnersCall({
      textKey,
      name,
    });
    partnerField.onChange(newValue);
    window.freshpaint.track('Created Parent', { name: value });
  };

  return (
    <FormField
      required
      label={t('library')}
      name="partner"
      render={({ field, muiprops }) => (
        <AutocompleteWithEdit
          options={options}
          value={field.value}
          onClear={() => {
            field.onChange('');
            setTerm('');
          }}
          onChange={(nextValue: TOption) => {
            field.onChange(nextValue);
          }}
          onInputChange={debouncedSetSearch}
          loading={isLoading}
          onCreate={isCreateMode ? handleCreatePartner : undefined}
          inputProps={{
            error: Boolean(muiprops.error),
            id: 'partner',
          }}
        />
      )}
    />
  );
}
